import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type {
	StartAPetitionAsyncPetitionDataQuery,
	StartAPetitionAsyncPetitionDataQueryVariables,
} from './api.graphql';
import { StartAPetitionAsyncPetitionData } from './api.graphql';

type Petition = NonNullable<StartAPetitionAsyncPetitionDataQuery['petition']>;
type FreePromotion = NonNullable<Petition['freePromotions']>[number];
type QualityScore = Extract<NonNullable<Petition['qualityScore']>, { __typename: 'QualityScore' }>;
type WithFreePromotion = Readonly<{ freePromotion: FreePromotion }>;
type WithQualityScore = Readonly<{ qualityScore: QualityScore }>;
type WithBoth = Readonly<{ freePromotion: FreePromotion; qualityScore: QualityScore }>;

// Union of all possible return types
type StartAPetitionAsyncPetitionDataResponse = WithFreePromotion | WithQualityScore | WithBoth;

type GetAsyncPetitionDataParams = {
	petitionId: string;
	fetchStarterFreePromotion: boolean;
	fetchQualityScore: boolean;
};
// eslint-disable-next-line complexity
export async function getAsyncPetitionData(
	{ petitionId, fetchStarterFreePromotion, fetchQualityScore }: GetAsyncPetitionDataParams,
	{ gql: { fetch } }: UtilityContext,
): Promise<StartAPetitionAsyncPetitionDataResponse | undefined> {
	try {
		const { data } = await fetch<StartAPetitionAsyncPetitionDataQuery, StartAPetitionAsyncPetitionDataQueryVariables>({
			query: StartAPetitionAsyncPetitionData,
			variables: { slugOrId: petitionId, fetchStarterFreePromotion, fetchQualityScore },
			rejectOnError: false,
		});

		const freePromotion = data?.petition?.freePromotions?.find(
			(promotion) => promotion.sourceLocation === 'auto_free_promotion',
		);
		const qualityScore =
			data?.petition?.qualityScore?.__typename === 'QualityScore' ? data.petition.qualityScore : undefined;

		// if both are requested, only return if both are available
		if (fetchStarterFreePromotion && fetchQualityScore) {
			return freePromotion && qualityScore ? { freePromotion, qualityScore } : undefined;
		}

		// if only one is requested, return if resolved with the value
		if (fetchStarterFreePromotion && freePromotion) return { freePromotion };
		if (fetchQualityScore && qualityScore) return { qualityScore };

		return undefined;
	} catch {
		// do nothing, we don't care if this fails
		return undefined;
	}
}
